import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ErrorResponse } from '../../custom-fetch';
import { Route } from '../../routes';
import { QueryKey } from '../../services/query-keys';
import { useOrganizationsService } from '../../services/organizations-service';
import ErrorsAlert from '../ErrorsAlert/ErrorsAlert';
import FormButtons from '../ui/FormButtons/FormButtons';
import { ECommonValue, EInputType, IInputField } from '../ui/Input/Input';
import { useInputs } from '../ui/Input/useInputs';
import IOrganization from '../../shared/IOrganization';
import countryOptions from '../../shared/countryOptions';
import UsersList from '../Users/UsersList';
import PageHeading from '../ui/PageHeading/PageHeading';
import AuditTrailList from '../AuditTrail/AuditTrailList';

interface IProps {
  id: string;
  organization?: IOrganization;
}

enum EInputs {
  name = "name",
  streetAddress = "streetAddress",
  zip = "zip",
  city = "city",
  country = "country",
  contactTitle = "contactTitle",
  contactPerson = "contactPerson",
  contactPhone = "contactPhone",
  contactEmail = "contactEmail",
  businessID = "businessID",
  apiAgent = "apiAgent",
  apiCode = "apiCode",
  repCode = "repCode",
  cdp = "cdp",
  apiKey = "apiKey",
  forcedCdp = "forcedCdp",
  title = "title",
  description = "description",
  notes = "notes",
  sendConfirmation = "sendConfirmation",
  confirmationEmail = "confirmationEmail",
  consumerRate = "consumerRate",
}

const OrganizationEdit: React.FC<IProps> = ({ id, organization }) => {
  const isEdit = id !== "add";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { updateOrganization, saveOrganization, deleteOrganization, validateApiKey } = useOrganizationsService();

  const [apiKeyMessage, setApiKeyMessage] = useState<string | null>(null);
  const [isValidatingApiKey, setIsValidatingApiKey] = useState(false);
  const originalApiKey = useRef<string | null>(null);
  const lastValidatedApiKey = useRef<string | null>(null);

  const { mutate, isPending, isError, error } = useMutation<
    IOrganization,
    ErrorResponse,
    IOrganization
  >({
    mutationFn: (data: IOrganization) => isEdit ? updateOrganization(id, data) : saveOrganization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.organizations] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.organizations, id] });
      navigate(Route.organizations);
    },
  });

  const {
    mutate: deleteMutate,
    isPending: isDeleting,
    isError: isDeletingError,
    error: deletingError,
  } = useMutation<boolean, ErrorResponse>({
    mutationFn: () => deleteOrganization(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.organizations] });
      navigate(Route.organizations);
    },
  });

  const [inputs, setInputs] = useState<IInputField>({
    [EInputs.name]: {
      type: EInputType.text,
      label: "Name",
      value: "",
      validation: { required: true },
    },
    [EInputs.streetAddress]: {
      type: EInputType.text,
      label: "Street Address",
      value: "",
    },
    [EInputs.zip]: {
      type: EInputType.text,
      label: "Zip Code",
      value: "",
    },
    [EInputs.city]: {
      type: EInputType.text,
      label: "City",
      value: "",
    },
    [EInputs.country]: {
      type: EInputType.reactSelect,
      label: "Country",
      value: "",
      options: countryOptions,
    },
    [EInputs.contactTitle]: {
      type: EInputType.text,
      label: "Contact Title",
      value: "",
    },
    [EInputs.contactPerson]: {
      type: EInputType.text,
      label: "Contact Person",
      value: "",
    },
    [EInputs.contactPhone]: {
      type: EInputType.tel,
      label: "Contact Phone",
      value: "",
    },
    [EInputs.contactEmail]: {
      type: EInputType.email,
      label: "Contact Email",
      value: "",
    },
    [EInputs.businessID]: {
      type: EInputType.text,
      label: "Business ID",
      value: "",
    },
    [EInputs.apiAgent]: {
      type: EInputType.text,
      label: "Wheels API Agent",
      value: "",
    },
    [EInputs.apiCode]: {
      type: EInputType.text,
      label: "Wheels API Code",
      value: "",
    },
    [EInputs.repCode]: {
      type: EInputType.text,
      label: "Wheels Rep Code",
      value: "",
    },
    [EInputs.cdp]: {
      type: EInputType.text,
      label: "CDP Override",
      value: "",
    },
    [EInputs.forcedCdp]: {
      type: EInputType.checkbox,
      label: "Forced CDP",
      value: [],
      options: [{ value: ECommonValue.YES, label: "" }],
    },
    [EInputs.apiKey]: {
      type: EInputType.text,
      label: "API Key",
      value: "",
    },
    [EInputs.title]: {
      type: EInputType.text,
      label: "Title",
      value: "",
    },
    [EInputs.description]: {
      type: EInputType.textarea,
      label: "Description",
      value: "",
    },
    [EInputs.notes]: {
      type: EInputType.textarea,
      label: "Notes",
      value: "",
    },
    [EInputs.sendConfirmation]: {
      type: EInputType.checkbox,
      label: "Send Confirmation",
      value: [],
      options: [{ value: ECommonValue.YES, label: "" }],
    },
    [EInputs.confirmationEmail]: {
      type: EInputType.email,
      label: "Confirmation Email",
      value: "",
    },
    [EInputs.consumerRate]: {
      type: EInputType.checkbox,
      label: "Consumer Rate (Kuluttajahinnat)",
      value: [],
      options: [{ value: ECommonValue.YES, label: "" }],
    },
  });

  const formattedData = useMemo(() => {
    if (!organization) return undefined;
    return {
      ...organization,
      forcedCdp: organization.forcedCdp ? [ECommonValue.YES] : [],
      sendConfirmation: organization.sendConfirmation ? [ECommonValue.YES] : [],
      consumerRate: organization.consumerRate ? [ECommonValue.YES] : [],
    };
  }, [organization]);

  const { createInput, submit } = useInputs({
    data: formattedData,
    inputs,
    setInputs,
  });

  const apiKey = inputs[EInputs.apiKey].value;

  useEffect(() => {
    if (organization && originalApiKey.current === null) {
      originalApiKey.current = organization.apiKey || "";
    }
  }, [organization]);

  useEffect(() => {
    if (apiKey !== null && apiKey !== undefined) {
      const apiKeyStr = apiKey as string;
      
      if (apiKeyStr === originalApiKey.current) {
        setApiKeyMessage(null);
        lastValidatedApiKey.current = apiKeyStr;
        return;
      }

      if (apiKeyStr && apiKeyStr !== lastValidatedApiKey.current) {
        const validate = async () => {
          setIsValidatingApiKey(true);
          try {
            const result = await validateApiKey(apiKeyStr, originalApiKey.current || "");
            setApiKeyMessage(result);
          } catch (err: any) {
            if (err instanceof ErrorResponse) {
              setApiKeyMessage(err.messages[0]);
            } else {
              setApiKeyMessage("Error validating API key");
            }
          } finally {
            setIsValidatingApiKey(false);
            lastValidatedApiKey.current = apiKeyStr;
          }
        };

        const timer = setTimeout(validate, 500);
        return () => clearTimeout(timer);
      } else if (!apiKeyStr) {
        setApiKeyMessage(null);
        lastValidatedApiKey.current = null;
      }
    }
  }, [apiKey, validateApiKey]);

  const forcedCdpValue = inputs[EInputs.forcedCdp].value;
  useEffect(() => {
    const isForcedCdp = !!(forcedCdpValue && (forcedCdpValue as string[]).indexOf(ECommonValue.YES) !== -1);
    setInputs((prev) => {
      const currentCdp = prev[EInputs.cdp];
      if (currentCdp.validation?.required === isForcedCdp) return prev;
      return {
        ...prev,
        [EInputs.cdp]: {
          ...currentCdp,
          validation: { ...currentCdp.validation, required: isForcedCdp },
        },
      };
    });
  }, [forcedCdpValue, setInputs]);
  
  const autoConfirmValue = inputs[EInputs.sendConfirmation].value;
  useEffect(() => {
    const isAutoConfirm = !!(autoConfirmValue && (autoConfirmValue as string[]).indexOf(ECommonValue.YES) !== -1);
    setInputs((prev) => {
      const currentEmail = prev[EInputs.confirmationEmail];
      if (currentEmail.validation?.required === isAutoConfirm) return prev;
      return {
        ...prev,
        [EInputs.confirmationEmail]: {
          ...currentEmail,
          validation: { ...currentEmail.validation, required: isAutoConfirm },
        },
      };
    });
  }, [autoConfirmValue, setInputs]);

  const submitHandler = async () => {
    const data = await submit();
    if (data) {
      const formattedSubmitData = {
        ...(data as any),
        forcedCdp: !!(data.forcedCdp && (data.forcedCdp as string[]).indexOf(ECommonValue.YES) !== -1),
        sendConfirmation: !!(data.sendConfirmation && (data.sendConfirmation as string[]).indexOf(ECommonValue.YES) !== -1),
        consumerRate: !!(data.consumerRate && (data.consumerRate as string[]).indexOf(ECommonValue.YES) !== -1),
      };
      mutate(formattedSubmitData as IOrganization);
    }
  };

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
        <div>
          {createInput(EInputs.name)}
          {createInput(EInputs.streetAddress)}
          {createInput(EInputs.zip)}
          {createInput(EInputs.contactPerson)}
          {createInput(EInputs.contactPhone)}
          {createInput(EInputs.apiAgent)}
          {createInput(EInputs.repCode)}
        </div>
        <div>
          {createInput(EInputs.businessID)}
          {createInput(EInputs.country)}
          {createInput(EInputs.city)}
          {createInput(EInputs.contactTitle)}
          {createInput(EInputs.contactEmail)}
          {createInput(EInputs.apiCode)}
          {createInput(EInputs.apiKey)}
          {isValidatingApiKey && <div style={{ fontSize: "0.8rem", color: "#666" }}>Validating API key...</div>}
          {apiKeyMessage && (
            <div style={{ 
              fontSize: "0.8rem", 
              color: apiKeyMessage.toLowerCase().includes("valid") ? "green" : "red",
              marginBottom: "0.5rem"
            }}>
              {apiKeyMessage}
            </div>
          )}
        </div>
      </div>
      
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginTop: "1rem" }}>
        {createInput(EInputs.forcedCdp)}
        {!!(forcedCdpValue && (forcedCdpValue as string[]).includes(ECommonValue.YES)) ? createInput(EInputs.cdp) : <div />}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
        {createInput(EInputs.sendConfirmation)}
        {!!(autoConfirmValue && (autoConfirmValue as string[]).includes(ECommonValue.YES)) ? createInput(EInputs.confirmationEmail) : <div />}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
        {createInput(EInputs.consumerRate)}
        <div />
      </div>
      {createInput(EInputs.title)}
      {createInput(EInputs.description)}
      {createInput(EInputs.notes)}
      
      {isError && error && <ErrorsAlert errors={error.messages} />}
      {isDeletingError && deletingError && <ErrorsAlert errors={deletingError.messages} />} 
      
      <FormButtons
        onSubmit={submitHandler}
        isLoading={isPending}
        onDelete={isEdit ? () => deleteMutate() : undefined}
        isDeleting={isDeleting}
        deleteConfirmMessage="Are you sure you want to delete this organization?"
      />

      {isEdit && (
        <>
        <div style={{ marginTop: "2rem" }}>
          <PageHeading onAdd={() => navigate(Route.userAddWithOrganization(id))}>
            Users
          </PageHeading>
          <UsersList organizationId={id} />
        </div>
        <AuditTrailList kind="Organization" targetId={id} />
        </>
      )}
    </>
  );
};

export default OrganizationEdit;
